import { getCooperlyAppBaseUrl } from "@/lib/app-base-url";

export type ComparisonAudience = "teams";

// Keep this small: the manual landing client only needs identities and attribution.
export const comparisonIdentities = [
  { slug: "cooperly-vs-windmill", competitor: "Windmill", audience: "teams" },
  { slug: "cooperly-vs-steady", competitor: "Steady", audience: "teams" },
  { slug: "cooperly-vs-fellow", competitor: "Fellow", audience: "teams" },
  { slug: "cooperly-vs-range", competitor: "Range", audience: "teams" },
  { slug: "cooperly-vs-geekbot", competitor: "Geekbot", audience: "teams" },
  { slug: "cooperly-vs-peoplebox", competitor: "Peoplebox", audience: "teams" },
  { slug: "cooperly-vs-lattice", competitor: "Lattice", audience: "teams" },
  { slug: "cooperly-vs-workleap", competitor: "Workleap", audience: "teams" },
  {
    slug: "cooperly-vs-officevibe",
    competitor: "Officevibe",
    audience: "teams",
  },
] as const satisfies readonly {
  slug: string;
  competitor: string;
  audience: ComparisonAudience;
}[];

export type ComparisonSlug = (typeof comparisonIdentities)[number]["slug"];

export function getComparisonIdentity(slug: string | null | undefined) {
  return comparisonIdentities.find((entry) => entry.slug === slug);
}

export function buildComparisonPath(
  slug: ComparisonSlug,
): `/compare/${ComparisonSlug}` {
  return `/compare/${slug}`;
}

export function buildComparisonManualHref(slug: ComparisonSlug) {
  return `/create-a-manual-for-yourself?${new URLSearchParams({ comparison: slug })}`;
}

export function buildComparisonManualContext(
  slug: string | null | undefined,
  ctaContext: string,
) {
  const entry = getComparisonIdentity(slug);
  return entry ? `comparison.${entry.slug}.${ctaContext}` : ctaContext;
}

export function buildComparisonTrialHref(
  slug: ComparisonSlug,
  placement: string,
) {
  const url = new URL(`${getCooperlyAppBaseUrl()}/register`);
  url.search = new URLSearchParams({
    utm_source: "cooperly_ai_site",
    utm_medium: "website",
    utm_campaign: "comparison_trial",
    utm_content: slug,
    acquisition_campaign: "comparison_trial",
    acquisition_cta_id: `compare_${slug}_${placement}`,
  }).toString();
  return url.toString();
}

export function buildComparisonHubTrialHref(placement: string) {
  const url = new URL(`${getCooperlyAppBaseUrl()}/register`);
  url.search = new URLSearchParams({
    utm_source: "cooperly_ai_site",
    utm_medium: "website",
    utm_campaign: "comparison_trial",
    utm_content: "compare-hub",
    acquisition_campaign: "comparison_trial",
    acquisition_cta_id: `compare_hub_${placement}`,
  }).toString();
  return url.toString();
}

/** Carry original acquisition through an internal hop without inventing new UTMs. */
export function preserveCampaignParams(href: string, currentUrl: string) {
  const current = new URL(currentUrl);
  const destination = new URL(href, current.origin);
  if (destination.origin !== current.origin) return href;

  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
  ]) {
    const value = current.searchParams.get(key);
    if (value && !destination.searchParams.has(key))
      destination.searchParams.set(key, value);
  }
  return `${destination.pathname}${destination.search}${destination.hash}`;
}
